import { useContext, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Label } from '@zillow/constellation-10';
import { cloneDeep } from 'es-toolkit';

import { useSelector } from '../../../../selectors/useSelector';
import { PropertiesSelectors } from '../../../../selectors';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';

import AdditionalAmenities from '../../../../components/postingPath/AdditionalAmenities';
import WizardAlert from '../../../../components/WizardAlert';

import type { ApartmentAmenityProps } from '../../../../model/ApartmentAmenity';

const label = 'Additional amenities';

type TagProps = Omit<ApartmentAmenityProps, 'availableInSelectUnits'>;

type Props = { isRelistingFieldset?: boolean };

const AddAmenitiesFormField = ({ isRelistingFieldset }: Props) => {
  const dispatch = useDispatch();
  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const { userFeaturesCanUpdateListing } = useSelector(
    (state) => ({
      userFeaturesCanUpdateListing: state?.user?.features?.canUpdateListing,
    }),
    shallowEqual,
  );

  const {
    propertyAmenities,
    listingAlias,
    isFeed,
    isArchived,
    restrictions: { isUpdateListingRestricted },
  } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const [errorObj, setErrorObj] = useState<{ additionalAmenities?: string }>({ additionalAmenities: '' });

  const isInputFormDisabled = isFeed || !userFeaturesCanUpdateListing || isArchived || isUpdateListingRestricted;
  const labelSize = isRelistingFieldset ? 'lg' : 'md';

  const updateCurrentProperty = (data: { propertyAmenities: Array<TagProps> }) =>
    dispatch(PropertiesActions.updateCurrentProperty({ propertyAmenities: cloneDeep(data.propertyAmenities) }));

  const handleError = (errorObj: { additionalAmenities?: string } = { additionalAmenities: '' }) => {
    // this is tied to a legacy component, AdditionalAmenities
    // errors are always handled at the field level within the component without WizardAlert
    setErrorObj(errorObj);
  };

  const updateFetchingStatus = (isFetching = false) => {
    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: isFetching });
  };

  return (
    <div data-testid="AddAmenitiesFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      {/* always display the label in other contexts, AdditionalAmenities, does not display the label if there are no amenities */}
      {propertyAmenities.length === 0 && (
        <Label size={labelSize} disabled={isFeed}>
          {label}
        </Label>
      )}

      <AdditionalAmenities
        errorObj={errorObj}
        isInputFormDisabled={isInputFormDisabled}
        label={label}
        labelSize={labelSize}
        listingAlias={listingAlias}
        previouslySavedAmenities={propertyAmenities}
        updateFormData={updateCurrentProperty}
        updateErrorObj={handleError}
        updateFetchingStatus={updateFetchingStatus}
      />
    </div>
  );
};

export default AddAmenitiesFormField;
